import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { Check, ArrowRight, ChevronDown, Sparkles, Gift } from "lucide-react";
import { useState } from "react";
import { useAppStore } from "@/store";
import { PaywallModal } from "@/components/paywall-modal";
import { GiftModal } from "@/components/gift-modal";

const FREE_FEATURES = [
  { text: "15 free actions", included: true },
  { text: "Full platform access (all 5 platforms)", included: true },
  { text: "Unlimited voice training", included: true },
  { text: "Post Generator", included: true },
  { text: "Smart Reply", included: true },
  { text: "Creator Studio (20+ formats)", included: true },
  { text: "Unlimited post history", included: false },
  { text: "Priority AI generation", included: false },
];

const PRO_FEATURES = [
  { text: "Unlimited posts", included: true },
  { text: "All 5 platforms", included: true },
  { text: "Full voice training (unlimited samples)", included: true },
  { text: "Post Generator", included: true },
  { text: "Smart Reply", included: true },
  { text: "Creator Studio (20+ formats)", included: true },
  { text: "Unlimited post history + Idea Vault", included: true },
  { text: "Priority AI generation", included: true },
];

const FAQS = [
  {
    q: "Can I cancel anytime?",
    a: "Yes — cancel anytime from your account settings. No penalties, no lock-in. Your posts and voice profile are retained for 30 days after cancellation in case you change your mind.",
  },
  {
    q: "What counts as a free action?",
    a: "Any AI generation uses one action: a post, a Smart Reply, a Creator Studio piece, or a creator tool (hooks, threads, CTAs, etc.). Editing content you've already generated doesn't count. Voice training never counts — train as much as you want for free.",
  },
  {
    q: "Is there a free trial for Pro?",
    a: "Instead of a time-limited trial, ScribPost gives you 15 completely free actions — no credit card required. You get full platform access to try everything: posts, Smart Reply, Creator Studio, and creator tools. Upgrade to Pro when you're ready for unlimited.",
  },
  {
    q: "What payment methods do you accept?",
    a: "We accept all major credit and debit cards (Visa, Mastercard, Amex, Discover) through Stripe's secure payment processing. We don't store card details ourselves.",
  },
  {
    q: "Does ScribPost work for content outside LinkedIn?",
    a: "Yes. All five platforms are available on the free trial too — LinkedIn, Twitter/X, Instagram, Facebook, and Reddit. Pro removes the action limit and unlocks unlimited history.",
  },
  {
    q: "Can I use ScribPost for a team or agency?",
    a: "Currently ScribPost is designed for individual creators and professionals. Team plans are on the roadmap — if you're interested, email us at hello@scribpost.com and we'll add you to the early access list.",
  },
  {
    q: "What happens to my voice profile if I downgrade?",
    a: "Your voice profile is saved even if you downgrade to the free plan. You'll retain your training data — you just won't be able to generate new posts until you upgrade again or if you still have free posts remaining.",
  },
];

const pricingFaqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
};

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 rounded-2xl overflow-hidden">
      <button
        className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 hover:bg-white/5 transition-colors"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="font-semibold text-white text-sm md:text-base">{q}</span>
        <ChevronDown
          className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-6 pb-5 text-slate-300 text-sm leading-relaxed">{a}</div>
      )}
    </div>
  );
}

export default function PricingPage() {
  const [billing, setBilling] = useState<"monthly" | "annual">("monthly");
  const [showPaywall, setShowPaywall] = useState(false);
  const [showGift, setShowGift] = useState(false);
  const { authToken, isSubscribed } = useAppStore();

  const isAnnual = billing === "annual";
  const stripePlan = isAnnual ? "yearly" : "monthly";
  const monthlyPrice = isAnnual ? "$6.40" : "$8";
  const annualTotal = "$76.80";

  const handleProCTA = () => {
    if (!isSubscribed) {
      setShowPaywall(true);
    }
  };

  return (
    <>
      <Helmet>
        <title>Pricing — ScribPost AI Post Generator</title>
        <meta name="description" content="Start free with 15 actions — no credit card. Full platform access including all 5 platforms, Creator Studio, and Smart Reply. Upgrade to ScribPost Pro for $8/month for unlimited." />
        <link rel="canonical" href="https://scribpost.com/pricing" />
        <script type="application/ld+json">{JSON.stringify(pricingFaqJsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-[#0D1A2A] text-white">
        {/* Nav */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0D1A2A]/90 backdrop-blur-md border-b border-white/5">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
            <Link href="/" className="flex items-center gap-2 font-bold text-xl">
              <span className="text-cyan-400">Scrib</span><span className="text-white">Post</span>
            </Link>
            <div className="hidden md:flex items-center gap-6 text-sm text-slate-300">
              <Link href="/pricing" className="text-white font-medium">Pricing</Link>
              <Link href="/blog" className="hover:text-white transition-colors">Blog</Link>
              <Link href="/about" className="hover:text-white transition-colors">About</Link>
            </div>
            <div className="flex items-center gap-3">
              {!authToken && (
                <Link href="/login" className="text-sm text-slate-300 hover:text-white transition-colors hidden sm:block">
                  Log in
                </Link>
              )}
              <Link
                href={authToken ? "/" : "/start"}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 transition-opacity text-white"
              >
                {authToken ? "Go to app" : "Start Free"}
              </Link>
            </div>
          </div>
        </nav>

        <div className="pt-32 pb-20 px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="text-center mb-10">
              <h1 className="text-4xl sm:text-5xl font-bold mb-4">Simple, Honest Pricing</h1>
              <p className="text-slate-400 text-lg max-w-xl mx-auto">
                Start free. No credit card. Upgrade when you're ready for unlimited.
              </p>
            </div>

            {/* Billing toggle */}
            <div className="flex justify-center mb-10">
              <div className="relative flex items-center bg-white/5 border border-white/10 rounded-full p-1 gap-1">
                <button
                  onClick={() => setBilling("monthly")}
                  className={`relative px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                    billing === "monthly"
                      ? "text-white"
                      : "text-slate-400 hover:text-slate-200"
                  }`}
                >
                  {billing === "monthly" && (
                    <span
                      className="absolute inset-0 rounded-full"
                      style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}
                      aria-hidden="true"
                    />
                  )}
                  <span className="relative">Monthly</span>
                </button>
                <button
                  onClick={() => setBilling("annual")}
                  className={`relative flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                    billing === "annual"
                      ? "text-white"
                      : "text-slate-400 hover:text-slate-200"
                  }`}
                >
                  {billing === "annual" && (
                    <span
                      className="absolute inset-0 rounded-full"
                      style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}
                      aria-hidden="true"
                    />
                  )}
                  <span className="relative">Annual</span>
                  <span className="relative text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                    SAVE 20%
                  </span>
                </button>
              </div>
            </div>

            {/* Plans */}
            <div className="grid md:grid-cols-2 gap-6 mb-20">
              {/* Pro */}
              <div className="rounded-2xl border border-cyan-500/30 p-8 bg-gradient-to-b from-cyan-500/5 to-blue-500/5 relative overflow-hidden">
                <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-xs font-bold text-white">
                  MOST POPULAR
                </div>
                <div className="mb-6">
                  <h2 className="text-xl font-bold mb-1 text-white">Pro</h2>
                  <p className="text-slate-400 text-sm">Unlimited everything. Your full voice.</p>
                </div>
                <div className="mb-1">
                  <span className="text-4xl font-black text-white">{monthlyPrice}</span>
                  <span className="text-slate-400 text-sm ml-2">/ month</span>
                  {isAnnual && (
                    <span className="ml-2 text-xs font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">
                      Save 20%
                    </span>
                  )}
                </div>
                {isAnnual ? (
                  <p className="text-slate-500 text-xs mb-6">
                    {annualTotal} billed annually
                  </p>
                ) : (
                  <p className="text-slate-500 text-xs mb-6">
                    or {annualTotal}/year with annual billing
                  </p>
                )}
                <div className="space-y-3 mb-8">
                  {PRO_FEATURES.map(({ text }) => (
                    <div key={text} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-cyan-500/15 border border-cyan-500/20 flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-cyan-400" />
                      </div>
                      <span className="text-sm text-slate-200">{text}</span>
                    </div>
                  ))}
                </div>

                {/* CTA — subscribed users go to settings; everyone else opens the paywall/signup modal */}
                {isSubscribed ? (
                  <Link
                    href="/settings"
                    className="block w-full text-center py-3 rounded-xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 transition-opacity text-white text-sm"
                  >
                    Manage subscription
                  </Link>
                ) : (
                  <button
                    onClick={handleProCTA}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 transition-opacity text-white text-sm"
                  >
                    <Sparkles className="w-4 h-4 shrink-0" />
                    {isAnnual ? `Go Pro — $6.40/month (billed ${annualTotal}/yr)` : "Go Pro — $8/month"}
                    <ArrowRight className="w-4 h-4 shrink-0" />
                  </button>
                )}
                <p className="text-center text-xs text-slate-500 mt-3">Cancel anytime. No contracts.</p>
              </div>

              {/* Free */}
              <div className="rounded-2xl border border-white/10 p-8 bg-white/[0.02]">
                <div className="mb-6">
                  <h2 className="text-xl font-bold mb-1 text-white">Free</h2>
                  <p className="text-slate-400 text-sm">Try the full platform free. No card needed.</p>
                </div>
                <div className="mb-6">
                  <span className="text-4xl font-black text-white">$0</span>
                  <span className="text-slate-400 text-sm ml-2">forever</span>
                </div>
                <div className="space-y-3 mb-8">
                  {FREE_FEATURES.map(({ text, included }) => (
                    <div key={text} className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${included ? "bg-cyan-500/15 border border-cyan-500/20" : "bg-white/5 border border-white/10"}`}>
                        {included ? (
                          <Check className="w-3 h-3 text-cyan-400" />
                        ) : (
                          <span className="text-slate-600 text-xs">✕</span>
                        )}
                      </div>
                      <span className={`text-sm ${included ? "text-slate-200" : "text-slate-500"}`}>{text}</span>
                    </div>
                  ))}
                </div>
                <Link
                  href={authToken ? "/" : "/start"}
                  className="block w-full text-center py-3 rounded-xl font-semibold border border-white/20 hover:bg-white/5 transition-colors text-white text-sm"
                >
                  {authToken ? "Go to app" : "Start Free"}
                </Link>
              </div>
            </div>

            {/* Gift a Membership */}
            <div className="max-w-xl mx-auto text-center mb-20">
              <div className="rounded-2xl border border-white/10 p-6 bg-white/[0.02]">
                <div className="text-3xl mb-3">🎁</div>
                <h3 className="text-lg font-bold mb-2">Gift a Membership</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Know someone who could use a writing upgrade? Give them the gift of ScribPost Pro.
                </p>
                <button
                  onClick={() => setShowGift(true)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 transition-colors"
                >
                  <Gift className="w-4 h-4" />
                  Gift a Membership
                </button>
              </div>
            </div>

            {/* FAQ */}
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-center mb-8">Pricing FAQ</h2>
              <div className="space-y-3">
                {FAQS.map(({ q, a }) => (
                  <FAQItem key={q} q={q} a={a} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="border-t border-white/10 py-10 px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <Link href="/" className="flex items-center gap-2 font-bold text-lg">
                <span className="text-cyan-400">Scrib</span><span className="text-white">Post</span>
              </Link>
              <nav className="flex flex-wrap justify-center gap-6 text-sm text-slate-400">
                <Link href="/about" className="hover:text-white transition-colors">About</Link>
                <Link href="/pricing" className="hover:text-white transition-colors">Pricing</Link>
                <Link href="/blog" className="hover:text-white transition-colors">Blog</Link>
                <Link href="/privacy" className="hover:text-white transition-colors">Privacy</Link>
                <Link href="/terms" className="hover:text-white transition-colors">Terms</Link>
              </nav>
              <p className="text-xs text-slate-600">© 2026 ScribPost. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>

      <PaywallModal
        show={showPaywall}
        onDismiss={() => setShowPaywall(false)}
        defaultPlan={stripePlan}
      />

      <GiftModal
        show={showGift}
        onDismiss={() => setShowGift(false)}
      />
    </>
  );
}
